import { PageHeader } from "../PageHeader";
import { SectionCard } from "../SectionCard";
import type { Connection } from "../../hooks/useConnection";

type Props = {
  connection: Connection;
  onJump: (tabId: string) => void;
};

const checkpoints: { label: string; description: string; target: string }[] = [
  {
    label: "1. Subir RouteCore",
    description:
      "Garanta que `make up-all` está rodando em routecore. O indicador 'RouteCore' na barra lateral deve ficar verde.",
    target: "examples",
  },
  {
    label: "2. Configurar API Key",
    description:
      "Defina ROUTECORE_API_KEY no .env do kitchen-sink (mesmo valor do .env do routecore).",
    target: "examples",
  },
  {
    label: "3. Carregar um exemplo",
    description:
      "Vá para 'Exemplos prontos', escolha small-valid-request e use 'Enviar como job' para disparar o solver.",
    target: "examples",
  },
  {
    label: "4. Acompanhar status",
    description:
      "Em 'Status do job', cole o job_id devolvido. O polling vai pintar o progresso até solved/partial/failed.",
    target: "job-status",
  },
  {
    label: "5. Visualizar no mapa",
    description:
      "Quando o job estiver em estado terminal com solução, abra 'Mapa' para ver as rotas desenhadas.",
    target: "map",
  },
];

const features: { title: string; bullets: string[] }[] = [
  {
    title: "Cenários cobertos",
    bullets: [
      "Caso feliz: small-valid + medium-valid",
      "Restrições: refrigerated e dangerous goods",
      "Limites: infeasible (sem capacidade)",
      "Erro: invalid-request para exercitar 422 do /validate",
    ],
  },
  {
    title: "Endpoints disponíveis",
    bullets: [
      "POST /jobs + GET status + GET result",
      "POST /validate (sem persistir)",
      "POST cancel + POST retry",
      "POST /matrix (OSRM) + /geocode + /reverse-geocode (Nominatim)",
    ],
  },
  {
    title: "Boas práticas demonstradas",
    bullets: [
      "Idempotency-Key automático no POST /jobs",
      "Polling progressivo no GET status",
      "Decoding de polyline + GeoJSON LineString",
      "Tratamento de status terminal vs transiente",
    ],
  },
];

export function TabOverview({ connection, onJump }: Props) {
  const upstreamLabel =
    connection.upstream === "ok"
      ? "RouteCore conectado"
      : connection.upstream === "error"
        ? "RouteCore offline — verifique 'make up-all'"
        : "verificando conexão com RouteCore...";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bem-vindo ao Kitchen Sink"
        subtitle="App de exemplos didáticos consumindo a API RouteCore como um cliente terceiro com chave de API."
      />

      <SectionCard
        title="Estado da conexão"
        description="O kitchen-sink é independente do RouteCore: ele depende apenas de uma chave de API válida e do endpoint estar acessível."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-ink-200 bg-ink-50/40 p-3 text-sm">
            <p className="font-semibold text-ink-800">BFF Go (este projeto)</p>
            <p className="mt-1 text-ink-600">
              Esconde a API key, injeta CORS e serve a lista de exemplos JSON. Roda em
              <code className="ks-code"> :8090</code>.
            </p>
          </div>
          <div className="rounded-lg border border-ink-200 bg-ink-50/40 p-3 text-sm">
            <p className="font-semibold text-ink-800">RouteCore (upstream)</p>
            <p className="mt-1 text-ink-600">{upstreamLabel}</p>
            {connection.upstreamUrl && (
              <p className="mt-1 text-[11px] text-ink-500">{connection.upstreamUrl}</p>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Caminho recomendado">
        <ol className="space-y-3">
          {checkpoints.map((c) => (
            <li key={c.label} className="flex gap-3">
              <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-800">
                {c.label[0]}
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-ink-900">{c.label.replace(/^\d+\.\s*/, "")}</p>
                <p className="text-sm text-ink-600">{c.description}</p>
                <button
                  type="button"
                  className="ks-btn-ghost mt-1 px-0 text-xs"
                  onClick={() => onJump(c.target)}
                >
                  → ir para esta tab
                </button>
              </div>
            </li>
          ))}
        </ol>
      </SectionCard>

      <div className="grid gap-3 lg:grid-cols-3">
        {features.map((f) => (
          <SectionCard key={f.title} title={f.title}>
            <ul className="space-y-1 text-sm text-ink-700">
              {f.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-brand-500" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        ))}
      </div>
    </div>
  );
}
